import { HistoryOutlined } from '@ant-design/icons';

import { Button, Skeleton } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { ngayGioVN } from '../../../../lib/dinh-dang';
import type { ThongBaoNhiemVu } from '../../types-nhiem-vu';
import { ActivitySummary } from './TaskActivityPanel';
import { UserAvatar } from './UserAvatar';

const SO_DONG_CHO = 3;

interface Props {
  items: ThongBaoNhiemVu[] | undefined;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  onOpen: (item: ThongBaoNhiemVu) => void;
}

export function TaskNotificationList({ items, loading, error, onRetry, onOpen }: Props) {
  const { t, locale } = useT();
  if (loading) {
    return (
      <div className="space-y-2 p-2">
        {Array.from({ length: SO_DONG_CHO }, (_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-center justify-between gap-2 p-4">
        <p className="text-sm text-danger-700">{t('vanBanNhiemVu.thongBao.loi')}</p>
        <Button size="sm" variant="outline" onClick={onRetry}>
          {t('chung.thuLai')}
        </Button>
      </div>
    );
  }
  if (!items || items.length === 0) {
    return (
      <div className="p-6 text-center">
        <p className="text-sm font-medium text-content-primary">{t('vanBanNhiemVu.thongBao.rong')}</p>
        <p className="mt-1 text-xs text-content-muted">{t('vanBanNhiemVu.thongBao.rongMo')}</p>
      </div>
    );
  }
  return (
    <ul className="divide-y divide-default">
      {items.map((n) => {
        const actor = n.actor_name ?? t('vanBanNhiemVu.hoatDong.heThong');
        return (
          <li key={n.id}>
            <button
              type="button"
              onClick={() => onOpen(n)}
              className={cn(
                'flex w-full gap-3 px-3 py-2.5 text-left hover:bg-subtle focus-visible:bg-subtle',
                n.unread && 'bg-primary-50',
              )}
            >
              {n.actor_id ? (
                <UserAvatar name={actor} className="mt-0.5" />
              ) : (
                <span
                  aria-hidden
                  className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-subtle text-xs text-content-muted"
                >
                  <HistoryOutlined />
                </span>
              )}
              <span className="min-w-0 flex-1">
                <span className="block text-sm text-content-secondary">
                  <span className="font-medium text-content-primary">{actor}</span> <ActivitySummary activity={n} />
                </span>
                <span className="block truncate text-xs text-content-primary">{n.task_title}</span>
                {n.content && (n.kind === 'COMMENT' || n.kind === 'PROGRESS') && (
                  <span className="block truncate text-xs text-content-muted">{n.content}</span>
                )}
                <span className="block text-2xs tabular-nums text-content-muted">{ngayGioVN(n.created_at, locale)}</span>
              </span>
              {n.unread && (
                <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary-600">
                  <span className="sr-only">{t('vanBanNhiemVu.thongBao.chuaDoc')}</span>
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
